import React, { useState, useEffect, useCallback } from 'react';
import vector from "../../assets/images/Vector.svg";
import ShowToast from 'components/ShowToast';
import Spinner from 'components/Spinner';
import PersonalizeModal from 'components/PersonalizeModal';
import { TYPE_SUCCESS, TYPE_FAIL, INTERVAL, emailPattern, READONLY } from "assets/constants/Constants";
import { createUserCall, getMetaDataCall, getUserRoleId, getUserEmailId } from "lib/axiosUtils";

// ------------------------------------------------------------
// Same API/data layer as the legacy SignUpPage:
//   - getMetaDataCall  -> populates the domain pills
//   - getUserRoleId    -> resolves the role id createUserCall needs
//   - getUserEmailId   -> blur + submit-time email uniqueness check
//   - createUserCall   -> creates the user record
//   - ShowToast        -> success/failure notifications
// ------------------------------------------------------------

const FEATURES = [
    {
        id: 'opportunity-map',
        title: 'AI Use Case Personalisation Engine',
        description: 'Discover tailored AI use cases designed for your specific business context.'
    },
    {
        id: 'agent-explorer',
        title: 'Agent Explorer',
        description: 'Review and shortlist AI agents evaluated across 150+ attributes.'
    },
    {
        id: 'demo-days',
        title: 'Invite-only Demo Days',
        description: 'Attend private demo sessions featuring top AI agents'
    }
];

// Shown on the confirmation screen after a successful submit —
// see NEXT_STEPS / renderSuccess() below.
const NEXT_STEPS = [
    {
        id: 'review-profile',
        title: 'We review your profile',
        description: 'Our team will review your details and understand your goals.',
        icon: 'user',
    },
    {
        id: 'match-usecases',
        title: 'We match your goal to relevant AI usecases',
        description: "You'll get access to curated usecases, benchmarks, and solution paths.",
        icon: 'star',
    },
    {
        id: 'email-next-steps',
        title: 'We send your early access next steps by email',
        description: 'Look out for an email from us with your personalised access',
        icon: 'mail',
    },
];

const INITIAL_FORM = {
    fullName: '',
    workEmail: '',
    companyName: '',
    domains: [],
};

// Common free/personal email providers we don't accept as "work" emails.
// Extend this list as needed — it's a blocklist, not an exhaustive
// whitelist of company domains, so anything not on this list is
// treated as a valid work domain.
const PERSONAL_EMAIL_DOMAINS = [
    'gmail.com', 'googlemail.com',
    'yahoo.com', 'yahoo.co.in', 'yahoo.co.uk', 'ymail.com',
    'outlook.com', 'hotmail.com', 'live.com', 'msn.com',
    'icloud.com', 'me.com', 'mac.com',
    'aol.com',
    'protonmail.com', 'proton.me',
    'zoho.com',
    'gmx.com', 'gmx.net',
    'mail.com',
    'yandex.com', 'yandex.ru',
    'rediffmail.com',
    'qq.com', '163.com', '126.com',
    'mail.ru',
    'naver.com',
    'web.de',
];

// True if the email's domain is NOT one of the common personal/free
// providers above (i.e. it looks like a company domain).
function isWorkEmail(email) {
    const domain = email.trim().toLowerCase().split('@')[1] || '';
    return domain.length > 0 && !PERSONAL_EMAIL_DOMAINS.includes(domain);
}

function CheckCircleIcon() {
    return (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="9" strokeWidth="2" />
            <path d="M8.5 12.5L10.75 14.75L15.5 10" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

function UserIcon() {
    return (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="8" r="3.5" strokeWidth="1.6" />
            <path d="M5 19.5c1.2-3.2 4-5 7-5s5.8 1.8 7 5" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
    );
}

function StarIcon() {
    return (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M12 3.5l2.47 5.24 5.78.66-4.3 3.95 1.2 5.7L12 16.1l-5.15 2.95 1.2-5.7-4.3-3.95 5.78-.66L12 3.5z"
                strokeWidth="1.6"
                strokeLinejoin="round"
            />
        </svg>
    );
}

function MailIcon() {
    return (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="3.5" y="5.5" width="17" height="13" rx="2" strokeWidth="1.6" />
            <path d="M4.5 7l7.5 6 7.5-6" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

const STEP_ICONS = {
    user: UserIcon,
    star: StarIcon,
    mail: MailIcon,
};

// Splits "Ada Lovelace" -> { firstName: 'Ada', lastName: 'Lovelace' }.
// Single-word names fall back to using the same word for both,
// matching how the legacy form treated first/last name as
// separate required fields.
function splitFullName(fullName) {
    const parts = fullName.trim().split(/\s+/);
    const firstName = parts[0] || '';
    const lastName = parts.length > 1 ? parts.slice(1).join(' ') : firstName;
    return { firstName, lastName };
}

// Same referral-code + OTP generation as the legacy SignUpPage.
function generateReferralCode(seed) {
    if (!seed) return "";
    const prefix = seed.slice(0, 3).toUpperCase();
    const randomPart = Math.random().toString(36).substring(2, 7).toUpperCase();
    return `${prefix}-${randomPart}`;
}

function generateOtp() {
    const allowedCharacters = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ!@#$%^&*()_+abcdefghijklmnopqrstuvwxyz';
    let otp = '';
    for (let i = 0; i < 8; i++) {
        const randomIndex = Math.floor(Math.random() * allowedCharacters.length);
        otp += allowedCharacters.substring(randomIndex, randomIndex + 1);
    }
    return otp;
}

// ------------------------------------------------------------
// Mixpanel helper — safe no-op if the snippet hasn't loaded yet
// (e.g. slow network, ad blocker) so tracking never breaks the form.
// The <script> snippet itself lives in public/index.html and only
// needs to be added once for the whole app.
// ------------------------------------------------------------
function trackEvent(eventName, props = {}) {
    if (typeof window !== 'undefined' && window.mixpanel && typeof window.mixpanel.track === 'function') {
        window.mixpanel.track(eventName, props);
    }
}

export default function SignupPage(props) {
    // ASSUMPTION: like the legacy page, an email can be prefilled
    // via the route (e.g. an invite link at /signup/:emailId).
    const prefilledEmail = props?.match?.params?.emailId || '';

    const [formData, setFormData] = useState({ ...INITIAL_FORM, workEmail: prefilledEmail });
    const [errors, setErrors] = useState({});
    const [status, setStatus] = useState('idle'); // idle | submitting | success | error
    const [statusMessage, setStatusMessage] = useState('');

    // Email captured at submit time, since formData is reset to
    // INITIAL_FORM right after a successful submit — PersonalizeModal
    // still needs it once it opens.
    const [submittedEmail, setSubmittedEmail] = useState('');
    const [showPersonalize, setShowPersonalize] = useState(false);

    // After 5 seconds on the success screen, open PersonalizeModal in
    // place instead of navigating away.
    useEffect(() => {
        let timer;
        if (status === 'success') {
            timer = setTimeout(() => {
                setShowPersonalize(true);
            }, 5000);
        }
        return () => clearTimeout(timer);
    }, [status]);

    const [domainOptions, setDomainOptions] = useState([]);
    const [userRoleId, setUserRoleId] = useState('');
    const [isLoading, setIsLoading] = useState(true);

    // Load domain pills (from metadata) + the role id createUserCall
    // needs, same two calls the legacy page made on mount.
    useEffect(() => {
        const loadInitialData = async () => {
            try {
                const [metadata, roleResponse] = await Promise.all([
                    getMetaDataCall({}, {}),
                    getUserRoleId({}, {}, READONLY),
                ]);

                const domains = Object.keys(
                    metadata?.data?.result?.[0]?.meta_data?.process_data?.classification_fields || {}
                );
                setDomainOptions(domains);
                setUserRoleId(roleResponse?.data?.result?.[0]?._id || '');
            } catch (error) {
                console.error('Failed to load signup metadata', error);
            } finally {
                setIsLoading(false);
            }
        };

        loadInitialData();
        trackEvent('Signup Page Viewed');
    }, []);

    const handleChange = useCallback((field) => (event) => {
        const { value } = event.target;
        setFormData((prev) => ({ ...prev, [field]: value }));
        setErrors((prev) => ({ ...prev, [field]: undefined }));
    }, []);

    const toggleDomain = useCallback((domain) => () => {
        setFormData((prev) => {
            const isSelected = prev.domains.includes(domain);
            return {
                ...prev,
                domains: isSelected
                    ? prev.domains.filter((d) => d !== domain)
                    : [...prev.domains, domain],
            };
        });
    }, []);

    // Same on-blur uniqueness check as the legacy emailUniqueHandler,
    // plus a work-email domain check so the person gets feedback
    // before they even try to submit.
    const handleEmailBlur = useCallback(async () => {
        const userEmail = formData.workEmail.trim().toLowerCase();
        if (!userEmail || !emailPattern.test(userEmail)) return;

        if (!isWorkEmail(userEmail)) {
            setErrors((prev) => ({
                ...prev,
                workEmail: 'Please use your company work email, not a personal email provider',
            }));
            return;
        }

        try {
            const found = await getUserEmailId({}, {}, userEmail);
            if (found?.data?.result?.length > 0) {
                ShowToast('You’re already in and registered for early access. Watch your inbox for launch updates!', TYPE_FAIL, INTERVAL, "", "");
                setErrors((prev) => ({ ...prev, workEmail: 'This work email is already registered' }));
            }
        } catch (error) {
            console.error('Email uniqueness check failed', error);
        }
    }, [formData.workEmail]);

    const validate = useCallback(async () => {
        const nextErrors = {};

        if (!formData.fullName.trim()) nextErrors.fullName = 'Full name is required';

        if (!formData.workEmail.trim()) {
            nextErrors.workEmail = 'Work email is required';
        } else if (!emailPattern.test(formData.workEmail.trim())) {
            nextErrors.workEmail = 'Enter a valid email address';
        } else if (!isWorkEmail(formData.workEmail.trim())) {
            nextErrors.workEmail = 'Please use your company work email, not a personal email provider';
        } else {
            const userEmail = formData.workEmail.trim().toLowerCase();
            try {
                const found = await getUserEmailId({}, {}, userEmail);
                if (found?.data?.result?.length > 0) {
                    nextErrors.workEmail = 'This work email is already registered';
                }
            } catch (error) {
                console.error('Email uniqueness check failed', error);
            }
        }

        if (!formData.companyName.trim()) nextErrors.companyName = 'Company name is required';

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    }, [formData]);

    const handleSubmit = useCallback(
        async (event) => {
            event.preventDefault();
            setStatusMessage('');

            const isValid = await validate();
            if (!isValid) {
                trackEvent('Signup Validation Failed');
                return;
            }

            setStatus('submitting');
            trackEvent('Signup Submitted', {
                companyName: formData.companyName.trim(),
                domains: formData.domains,
            });

            try {
                const { firstName, lastName } = splitFullName(formData.fullName);
                const userEmail = formData.workEmail.trim();

                const requestData = {
                    userFirstName: firstName,
                    userLastName: lastName,
                    userEmailId: userEmail,
                    userName: userEmail,
                    userRole: userRoleId,
                    userReferralCode: generateReferralCode(userEmail),
                    userOtp: generateOtp(),
                    userCompanyName: formData.companyName.trim(),
                    userDomains: formData.domains,
                };

                const userResponse = await createUserCall({}, requestData);

                if (userResponse?.statusCode === 200) {
                    ShowToast(
                        'User created successfully. Check your email.',
                        TYPE_SUCCESS,
                        INTERVAL,
                        "",
                        ""
                    );
                    setSubmittedEmail(userEmail);
                    setStatus('success');
                    setFormData(INITIAL_FORM);

                    trackEvent('Signup Success');
                    if (window.mixpanel && typeof window.mixpanel.identify === 'function') {
                        window.mixpanel.identify(userEmail);
                        window.mixpanel.people.set({
                            $email: userEmail,
                            $name: formData.fullName.trim(),
                            companyName: formData.companyName.trim(),
                        });
                    }
                } else {
                    throw new Error(userResponse?.message || 'Something went wrong. Please try again.');
                }
            } catch (error) {
                const message = error.message || 'Something went wrong. Please try again.';
                setStatus('error');
                setStatusMessage(message);
                trackEvent('Signup Failed', { reason: message });
                ShowToast(message, TYPE_FAIL, INTERVAL, "", "");
            }
        },
        [formData, userRoleId, validate]
    );

    const isSubmitting = status === 'submitting';

    const handleRedirectHome = useCallback(() => {
        window.location.href = "/index.html";
    }, []);

    // ------------------------------------------------------------
    // Success view — "Your request is in" / "What happens next"
    // Rendered instead of the form once status === 'success'.
    // ------------------------------------------------------------
    const renderSuccess = () => (
        <div className="signup-card signup-card--success">
            {/* Left — confirmation panel */}
            <div className="signup-panel-left">
                <div className="signup-panel-left__intro">
                    <span className="signup-panel-left__check">
                        <CheckCircleIcon />
                    </span>
                    <h2 className="signup-panel-left__heading">Your request is in</h2>
                    <p className="signup-panel-left__subtext">
                        Verify your email before we move forward
                    </p>
                </div>
            </div>

            {/* Right — what happens next */}
            <div className="signup-panel-right signup-panel-right--success">
                <div className="signup-panel-right__header signup-panel-right__header--center">
                    <h1 className="signup-panel-right__title">What happens next</h1>
                </div>

                <ul className="next-steps">
                    {NEXT_STEPS.map((step, index) => {
                        const Icon = STEP_ICONS[step.icon];
                        const isLast = index === NEXT_STEPS.length - 1;
                        return (
                            <React.Fragment key={step.id}>
                                <li className="next-step">
                                    <span className="next-step__icon">
                                        <Icon />
                                    </span>
                                    <div className="next-step__body">
                                        <p className="next-step__title">{step.title}</p>
                                        <p className="next-step__desc">{step.description}</p>
                                    </div>
                                </li>
                                {!isLast && <hr className="next-step__divider" />}
                            </React.Fragment>
                        );
                    })}
                </ul>
            </div>
        </div>
    );

    // ------------------------------------------------------------
    // Form view — "Apply for early access"
    // ------------------------------------------------------------
    const renderForm = () => (
        <div className="signup-card">
            {/* Left — glass info panel */}
            <div className="signup-panel-left">
                <div className="signup-panel-left__intro">
                    <h2 className="signup-panel-left__heading"> Put AI To Work </h2>

                    <p className="signup-panel-left__subtext"> Secure your spot among the first 100 enterprises to enable us
                        provide a personalized experience. </p>
                        <p className="signup-panel-left__subtext"> Over the next 90 days, we are putting the final touches to our
                        unified AI discovery platform.<br></br></p>
                </div>

                <ul className="signup-panel-left__features">
                    <p className="signup-panel-left__subtext">Here is what you will get upon Product launch.</p>
                    {FEATURES.map((feature) => (
                        <li className="feature-row" key={feature.id}>
                            <span className="feature-row__icon">
                                <CheckCircleIcon />
                            </span>
                            <div className="feature-row__body">
                                <p className="feature-row__title">{feature.title}</p>
                                <p className="feature-row__desc">{feature.description}</p>
                            </div>
                        </li>
                    ))}
                </ul>
            </div>

            {/* Right — form panel */}
            <div className="signup-panel-right">
                <div className="signup-panel-right__header">
                    <h1 className="signup-panel-right__title">Apply for early access</h1>
                    <p className="signup-panel-right__subtitle">
                        Takes under a minute.
                    </p>
                </div>

                <form className="signup-form" onSubmit={handleSubmit} noValidate>
                    <div className="form-field">
                        <label className="form-field__label" htmlFor="fullName">
                            Full Name<span className="form-field__required">*</span>
                        </label>
                        <input
                            id="fullName"
                            type="text"
                            className={`form-field__input${errors.fullName ? ' form-field__input--error' : ''}`}
                            placeholder="..."
                            value={formData.fullName}
                            onChange={handleChange('fullName')}
                            disabled={isSubmitting}
                        />
                        {errors.fullName && <span className="form-field__error">{errors.fullName}</span>}
                    </div>

                    <div className="form-field">
                        <label className="form-field__label" htmlFor="workEmail">
                            Work Email<span className="form-field__required">*</span>
                        </label>
                        <input
                            id="workEmail"
                            type="email"
                            className={`form-field__input${errors.workEmail ? ' form-field__input--error' : ''}`}
                            placeholder="name@company.com"
                            value={formData.workEmail}
                            onChange={handleChange('workEmail')}
                            onBlur={handleEmailBlur}
                            disabled={isSubmitting || Boolean(prefilledEmail)}
                        />
                        {errors.workEmail && <span className="form-field__error">{errors.workEmail}</span>}
                    </div>

                    <div className="form-field">
                        <label className="form-field__label" htmlFor="companyName">
                            Company Name<span className="form-field__required">*</span>
                        </label>
                        <input
                            id="companyName"
                            type="text"
                            className={`form-field__input${errors.companyName ? ' form-field__input--error' : ''}`}
                            placeholder="..."
                            value={formData.companyName}
                            onChange={handleChange('companyName')}
                            disabled={isSubmitting}
                        />
                        {errors.companyName && (
                            <span className="form-field__error">{errors.companyName}</span>
                        )}
                    </div>

                    <div className="form-field">
                        <label className="form-field__label">Domains to focus on</label>
                        <div className="domain-pills">
                            {domainOptions.map((domain) => {
                                const selected = formData.domains.includes(domain);
                                return (
                                    <button
                                        type="button"
                                        key={domain}
                                        className={`domain-pill${selected ? ' domain-pill--selected' : ''}`}
                                        onClick={toggleDomain(domain)}
                                        aria-pressed={selected}
                                        disabled={isSubmitting}
                                    >
                                        {domain}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div className="signup-form__actions">
                        <button type="submit" className="cta-button" disabled={isSubmitting}>
                            {isSubmitting ? 'Submitting…' : 'Request access'}
                            <img
                                src={vector}
                                alt=""
                                className="cta-button__icon"
                            />
                        </button>

                        {status === 'error' && statusMessage && (
                            <p className="signup-status signup-status--error" role="status">
                                {statusMessage}
                            </p>
                        )}

                        <p className="signup-consent">
                            I agree that million ai stores my contact details for the purpose
                            of scheduled meetings and follow-ups. By submitting the form, you
                            acknowledge our{' '}
                            <a href="/privacy-policy" target="_blank" rel="noreferrer">
                                Privacy Policy
                            </a>
                            . You can revoke this consent at any time.
                        </p>
                    </div>
                </form>
            </div>
        </div>
    );

    if (isLoading) {
        return <Spinner />;
    }

    // Once the 5s success timer elapses, render PersonalizeModal as a
    // full standalone page in place of the signup card, same pattern
    // ForgotPassword.js uses for its post-reset personalization step.
    if (showPersonalize) {
        return (
            <PersonalizeModal
                isOpen={true}
                userEmail={submittedEmail}
                onClose={handleRedirectHome}
                onSubmit={(data) => {
                    console.log("Personalization submitted on signup:", data);
                    handleRedirectHome();
                }}
            />
        );
    }

    return (
        <section className="signup-page">
            {status === 'success' ? renderSuccess() : renderForm()}
        </section>
    );
}
